import { currentIdentity } from '../persist/caseListPersistConfig';

/**
 * FIN-13485 — start redux-persist only once the current user is known.
 *
 * redux-persist writes every whitelisted key on the first store update after rehydration. If
 * persistence starts while nobody is logged in, the owner-aware reconciler (rightly) hands the
 * store the default filters and a null owner — and that first write then overwrites the stored
 * entry with exactly that. The reported flow hits this: a token refresh fails, the cleanup keeps
 * the filters, Keycloak logout lands on /login, and the app boots once with no session before
 * the login redirect — long enough to destroy the entry the investigator was meant to get back.
 *
 * So: identity present → start now (the normal Keycloak boot). Identity absent → do not create
 * the persistor; nothing can write. That boot is either redirecting to Keycloak login (no state
 * of interest) or showing the hybrid login page — for the latter, watch the store and start on
 * the first state change after the login has written the username, so the same-tab session
 * still persists its filters.
 *
 * Returns the persistor when started immediately, otherwise null.
 */
export const startPersistingWhenIdentified = (
  store,
  startPersisting,
  identityOf = currentIdentity
) => {
  if (identityOf()) {
    return startPersisting();
  }
  let started = false;
  const unsubscribe = store.subscribe(() => {
    if (started || !identityOf()) return;
    started = true;
    unsubscribe();
    startPersisting();
  });
  return null;
};

export default startPersistingWhenIdentified;
